import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './App.scss';
import type { INetworksResponse } from './interfaces/network.model';
import { defaultLocalizations, localizationNames } from './interfaces/localizations';
import { getLocalizedMessage } from './helpers/localizations';
import { useGet } from './custom-hooks/use-get';
import { useGetLocalizations } from './custom-hooks/use-get-localizations';
import 'react-loading-skeleton/dist/skeleton.css';
import type { IGasPriceModel } from './interfaces/gas-price.model';
import { NetworkList } from './components/network-list/NetworkList';
import { GasPriceSection } from './components/gas-price-section/GasPriceSection';
import SearchGasPrice from './components/search-gas-price/SearchGasPrice';
import { useInitializeApis } from './custom-hooks/use-initialize-apis';
import { useAppState } from './custom-hooks/use-app-state';
import { updateUrlParam } from './helpers/updateUrlParams';
import { useTimer } from './custom-hooks/useTimer';
import { useNetworkContainer } from './custom-hooks/useNetworkContainer';
import { useDeviceSize } from './custom-hooks/useDeviceSize';

export function App() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { networkApi, localizationApi, tokensApi, gasPriceApi, networkDescriptionApi } = useInitializeApis();
  const { data: networksData, loading } = useGet<INetworksResponse>(networkApi);
  const { currentNetwork, setCurrentNetwork, currentToken, setCurrentToken, currentTokenPrice, setCurrentTokenPrice, language } = useAppState(networksData);

  const [gasPriceApiReload, setGasPriceApiReload] = useState<string>(Math.random.toString());
  const isNativeSelectedFromUrl = searchParams.get('Native');

  const { data: gasPriceData, loading: gasPriceLoading } = useGet<IGasPriceModel>(`${gasPriceApi}/${currentNetwork}`, [currentNetwork, gasPriceApiReload]);

  const { data: tokensData } = useGet<IGasPriceModel>(`${tokensApi}${currentNetwork}`, [currentNetwork, gasPriceApiReload]);
  const { data: tokenPrice } = useGet<IGasPriceModel>(currentToken ? `${tokensApi}${currentNetwork}&token_id=${currentToken}` : null, [currentNetwork, gasPriceApiReload, currentToken]);
  const { data: networkDescription } = useGet<IGasPriceModel>(currentNetwork ? `${networkDescriptionApi}/${currentNetwork}` : null, [currentNetwork], {
    headers: {
      'ff-locale': `${language}`,
    },
  });

  useEffect(
    () => {
      if (currentToken == null) {
        setCurrentTokenPrice(null);
      } else {
        setCurrentTokenPrice(tokenPrice);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentToken, tokenPrice],
  );
  useEffect(() => {
    setCurrentToken(null);
    setCurrentTokenPrice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNetwork, isNativeSelectedFromUrl]);
  const [isTimerEnd, setIsTimerEnd] = useState(false);
  const { localizations } = useGetLocalizations(localizationApi, localizationNames, language, defaultLocalizations);
  const timer = useTimer(gasPriceData, localizations, setGasPriceApiReload);

  useEffect(() => {
    const interval = setTimeout(() => {
      setIsTimerEnd(true);
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  const { isMobile, isTablet, isMobileDeviceResized } = useDeviceSize();
  useNetworkContainer(isMobile, isMobileDeviceResized);

  const localizedMessage = (key: string): string => {
    return getLocalizedMessage(key, localizations);
  };

  const handleNetworkChange = (networkId: string): void => {
    localStorage.removeItem('currentNetwork');
    setCurrentNetwork(networkId);
    localStorage.setItem('currentNetwork', networkId);
  };

  const handleTokenChange = (tokenData: string): void => {
    updateUrlParam('tokenId', tokenData.toString(), navigate);
    setCurrentToken(tokenData);
  };

  const selectedNetwork = networksData?.networks.find((network) => network.nameId === currentNetwork);

  return (
    <Sentry.ErrorBoundary>
      <div className="calculator__content">
        <NetworkList
          networksData={networksData}
          loading={loading}
          currentNetwork={currentNetwork}
          handleNetworkChange={handleNetworkChange}
          isTimerEnd={isTimerEnd}
          localizedMessage={localizedMessage}
        />
        <SearchGasPrice
          gasPriceData={gasPriceData}
          loading={loading}
          localizedMessage={localizedMessage}
          tokensData={tokensData}
          handleNetworkChange={handleNetworkChange}
          handleTokenChange={handleTokenChange}
          currentNetwork={currentNetwork}
        />
        <div className="calculator__content--price-description">
          <GasPriceSection
            gasPriceData={gasPriceData}
            gasPriceLoading={gasPriceLoading}
            isTimerEnd={isTimerEnd}
            isTablet={isTablet}
            isMobile={isMobile}
            timer={timer}
            localizations={localizations}
            localizedMessage={localizedMessage}
            tokenPrice={currentTokenPrice}
            selectedNetwork={selectedNetwork}
            networkDescription={networkDescription}
          />
        </div>
      </div>
    </Sentry.ErrorBoundary>
  );
}
